// TODO: Needs furthur refactoring
const numConsts = {
    scale: {
        fh: 500,
        ot: 1000,
        tfh: 2500,
        ft: 5000,
        tt: 10000
    },
    percent: 100,
    maxPadding: 99,
    facts: {
        seven: 7,
        width: 8,
        depth: 9,
        wideSideInd: 10,
        structWide: 11
    }
};

export class Segment {
    segmentNumber: string;
    countyCode: string;
    stateRouteNumber: string;
    streetName: string;
    cumOffsetBegin: number;
    cumOffsetEnd: number;
    length: number;
    sideIndicator: string;
    latLongs: string;

    next: Segment;
    prev: Segment;
    otherSide: Segment;

    constructor(data: ISegmentData) {
        this.segmentNumber = data.segmentNumber;
        this.countyCode = data.countyCode;
        this.stateRouteNumber = data.stateRouteNumber;
        this.streetName = data.streetName;
        this.cumOffsetBegin = data.cumOffsetBegin;
        this.cumOffsetEnd = data.cumOffsetEnd;
        this.length = data.length;
        this.sideIndicator = data.sideIndicator;
        this.latLongs = data.latLongs;
    }

    isIntersectionForSelectedSegment(intersection: IIntersection): boolean {
        // Return true if intersection's segment matches selected segment <OR>
        // if the intersection's segment matches the next segment and is at offset 0 so
        // that we can see that intersection at the end of our current selected segment bar
        return intersection.segmentNumber === this.segmentNumber ||
            (intersection.segmentNumber === this.next.segmentNumber && this.next.segmentNumber && intersection.segmentOffset === 0);
    }
}

export class SegmentData {
    // Constants
    readonly intersectionTypesAboveOnly = ['AHEADL', 'ALENTR', 'ALEXIT', 'BACKL', 'BLENTR', 'BLEXIT', 'TLEFT', 'TLENTR', 'TLEXIT'];
    readonly intersectionTypesBelowOnly = ['AHEADR', 'ARENTR', 'AREXIT', 'BACKR', 'BRENTR', 'BREXIT', 'TRIGHT', 'TRENTR', 'TREXIT'];
    readonly colors = ['006', '060', '900', 'C30', '030', '066', '09C', 'C33', '960', '33C', 'C60',
        '930', '906', '909', '390', '369', '39F', '936', '396', '06C', 'C66', '000'];

    usedColors = [];

    // From data
    name: string;
    countyCode: string;
    countyName: string;
    sideIndicator: string;
    segmentBegin?: string;
    segmentEnd?: string;
    segments: Segment[];
    intersections: IIntersection[];
    attributes: any[];
    length: number;

    // Derived
    get scale(): IScale[] {
        const vals = [numConsts.scale.fh, numConsts.scale.ot, numConsts.scale.tfh, numConsts.scale.ft, numConsts.scale.tt].filter(val => val < this.segments[0].length);
        vals.push(this.length);
        return vals.map((val, i, arr) => ({
            value: val,
            width: ((i === 0 ? arr[i] : (arr[i] - arr[i - 1]))) / this.length * numConsts.percent
        } as IScale));
    }

    constructor(data: IRoute) {
        this.name = data.name;
        this.countyCode = data.countyCode;
        this.countyName = data.countyName;
        this.sideIndicator = data.sideIndicator;
        this.segmentBegin = data.segmentBegin;
        this.segmentEnd = data.segmentEnd;
        this.segments = data.segments.map(seg => new Segment(seg));
        this.intersections = this.getIntersections(data.intersections);
        this.length = data.length;
        this.attributes = this.getAttributes(data.attributes, this.segments[0], this.segments);
    }

    getAttributes(attributes: IAttribute[], segment: Segment, segments: Segment[]): IAttribute[] {
        if (attributes && segment && segments) {
            attributes.forEach((attr, attrIndex) => {
                attr.tablePartitionedByYear = attr.table.toUpperCase() === 'PAVEMENT_HISTORY';
                this.usedColors = [];

                attr.item.forEach((datum, index) => {
                    datum.isOverlap = false;
                    datum.boundedOffsetBegin = Math.max(datum.cumOffsetBegin, segment.cumOffsetBegin);
                    datum.boundedOffsetEnd = Math.min(datum.cumOffsetEnd, segment.cumOffsetEnd);
                    datum.attributeBarSize = datum.boundedOffsetEnd - datum.boundedOffsetBegin;

                    datum.hidden = false;
                    datum.padding = null;
                    datum.width = null;
                    datum.isSameYearAsLast = false;

                    //sets sameYearAsLast to break by year in pavement history
                    if (index !== 0) {
                        for (let j = index - 1; j >= 0; j--) {
                            if (!attr.item[j].hidden) {
                                if (attr.item[j].facts[0] === datum.facts[0]) {
                                    datum.isSameYearAsLast = true;
                                }
                                j = 0;
                            }
                        }
                    }

                    if ((datum.attributeBarSize === 0 && datum.boundedOffsetBegin === datum.boundedOffsetEnd) || datum.attributeBarSize < 0) {
                        datum.hidden = true;
                    } else {
                        datum.padding = Math.min((datum.boundedOffsetBegin - segment.cumOffsetBegin) / segment.length * numConsts.percent, numConsts.maxPadding);
                        if (index !== 0) {
                            //gets the previous items up till an overlapping item (get all items on the same row)
                            const allPrevious = this.previousUnhidden(attr.item, index);

                            //sets isOverlap if item is overlapping another that comes before it
                            if (attr.item[index - 1].cumOffsetEnd > datum.cumOffsetBegin || allPrevious.every(item => item.cumOffsetEnd > datum.cumOffsetBegin)) {
                                datum.isOverlap = true;
                            } else {
                                //changes padding based on the width and padding of items before on same line
                                if (allPrevious.length > 0) {
                                    datum.padding = Math.max(datum.padding - (allPrevious.flatMap(prevAttr => [prevAttr.width, prevAttr.padding]).reduce((a, b) => a + b)), 0);
                                }
                            }
                        }
                        datum.width = datum.attributeBarSize / segment.length * numConsts.percent;
                        if (datum.attributeBarSize !== 0) {
                            datum.color = this.getColor(datum.description);
                        }
                    }

                    //setup to seperate by year (ex. pavement history data)
                    if (datum.toolTip && datum.toolTip.indexOf('&#10') > -1) {
                        datum.toolTip = datum.toolTip.replace(/&#10/g, '\r\n');
                    }
                    datum.toolTip = datum.toolTip;
                });

                //checks if all items in attribute are hidden or not and sets property
                //gets evaluated in html
                if (attr.item.every(item => item.hidden)) {
                    attributes[attrIndex].hasAllHidden = true;
                } else {
                    attributes[attrIndex].hasAllHidden = false;
                }
            });
        }
        return attributes;
    }

    getIntersections(intersections: IIntersection[]): IIntersection[] {
        const reducedIntersections = intersections.reduce<IIntersection[]>((previousArray, currentStreet) => {
            const previousStreet = previousArray[previousArray.length - 1];
            if (previousStreet &&
                previousStreet.cumulativeOffset === currentStreet.cumulativeOffset &&
                (
                    this.intersectionTypesBelowOnly.includes(previousStreet.intersectionType) === this.intersectionTypesBelowOnly.includes(currentStreet.intersectionType) ||
                    this.intersectionTypesAboveOnly.includes(previousStreet.intersectionType) === this.intersectionTypesAboveOnly.includes(currentStreet.intersectionType)
                )
            ) {
                /* Add current street to previous street seperated by a <br> */
                previousStreet.streetName += ' / ' + currentStreet.streetName;
            } else {
                /* Just add intersection like normal; Its not overlapping with another */
                previousArray.push(currentStreet);
            }
            return previousArray;
        }, [] /* Start with an empty array */);
        // Set offset
        reducedIntersections.forEach(intersection => {
            intersection.offset = (intersection.cumulativeOffset - this.segments[0].cumOffsetBegin) / this.length * numConsts.percent;
        });
        return reducedIntersections;
    }

    fullDescription(item: IAttributeItem): string {
        let retVal = null;
        if (item.description !== null) {
            retVal = item.description;
            if (item.facts && item.facts.length > 0) {
                retVal += ` ${item.facts[0]}`;
                if (item.facts.length > 1) {
                    retVal += `, ${item.facts[1]}`;
                }
                //
                if (item.facts.length > numConsts.facts.width) { // Depth
                    retVal += `, ${item.facts[numConsts.facts.depth]}`;
                }
                //
                if (item.facts.length > numConsts.facts.wideSideInd) { // Structural/Widening and Widening Side Ind
                    retVal += `, ${item.facts[numConsts.facts.structWide]}, ${item.facts[numConsts.facts.wideSideInd]}`;
                } else if (item.facts.length > numConsts.facts.seven) { // Width
                    retVal += `, ${item.facts[numConsts.facts.width]}`;
                }
            }
        }
        return retVal;
    }

    previousUnhidden(items: IAttributeItem[], currentIndex: number): IAttributeItem[] {
        const retVal = [];

        //gets all items that come before current item
        items.forEach((item, index) => {
            if (index < currentIndex) {
                if (!item.hidden) {
                    retVal.push(item);
                }
            }
        });

        //cuts down list of items if one before overlaps
        //this gives us just the items on the same row as the current item
        retVal.slice().reverse().forEach((item, index) => {
            if (item.isOverlap) {
                retVal.splice(index, retVal.length - (index + 1));
            }
        });

        return retVal;
    }

    getColor(key) {
        let color = null;
        const keyOfColor = key;
        for (const row of this.usedColors) {
            if (row.key === keyOfColor) {
                color = row.value;
                break;
            }
        }

        if (color === null) {
            if (this.usedColors.length === 0) {
                color = this.colors[0];
            } else {
                let colorIndex = this.colors.indexOf(this.usedColors[this.usedColors.length - 1].value) + 1;
                if (colorIndex === this.colors.length) {
                    colorIndex = 0;
                }
                color = this.colors[colorIndex];
            }
            this.usedColors.push({ key: keyOfColor, value: color });
        }
        return color;
    }
}

export interface IIntersection {
    streetName: string;
    cumulativeOffset: number;
    offset: number;
    sideIndicator: string;
    segmentNumber: string;
    segmentOffset: number;
    type: string;
    stateRouteNumber: string;
    intersectionType: string;
}

export interface IRoute {
    name: string;
    countyCode: string;
    countyName: string;
    sideIndicator: string;
    segmentBegin: string;
    segmentEnd: string;
    segments: Segment[];
    intersections: IIntersection[];
    attributes: IAttribute[];
    length: number;
}

export interface IAttribute {
    name: string;
    description: string;
    table: string;
    order: string;
    item: IAttributeItem[];
    isIndicator: boolean;
    tablePartitionedByYear: boolean;
    hasAllHidden: boolean;
}

export interface IAttributeItem {
    isSameYearAsLast: boolean;
    color: string;
    width: number;
    attributeBarSize: number;
    boundedOffsetEnd: number;
    boundedOffsetBegin: number;
    value: string;
    description: string;
    title: string;
    toolTip: string;
    nlfId: string;
    cumOffsetBegin: number;
    cumOffsetEnd: number;
    facts: string[];
    routeLength: number;
    hidden: boolean;
    padding: number;
    isOverlap: boolean;
}

export interface ISegmentData {
    countyCode: string;
    cumOffsetBegin: number;
    cumOffsetEnd: number;
    latLongs: string;
    length: number;
    segmentNumber: string;
    sideIndicator: string;
    stateRouteNumber: string;
    streetName: string;
}

export interface IScale {
    value: number;
    width: number;
}

export interface IISLETemplateAttrib {
    table: string;
    column: string;
    sequence: number;
    description: string;
    modifiedDate: Date;
    isDefault: boolean;
}

export interface IISLETemplate {
    userId: string;
    name: string;
    isDefault: boolean;
    attributes: IISLETemplateAttrib[];
    isValid: boolean;
}
